import React, { ReactElement, useMemo } from 'react';
import { BulletListContent, BulletListIcon, Li, Spinner, StyleTextColor, Typography } from '@vp/swan';
import { Validation, ValidationState, ValidatorType } from '@vp/upload-and-print-validator';
import { useClientValidation } from 'src/clientValidation';
import { usePerfectBound } from 'src/hooks/usePerfectBound';
import { useValidationContext } from 'src/hooks/ValidationContextHooks';
import errorValidationIcon from 'src/images/validation/error-icon.svg';
import failedValidationBullet from 'src/images/validation/failedValidationBullet.svg';
import successfulValidationBullet from 'src/images/validation/success-icon.svg';
import unvalidatedBullet from 'src/images/validation/unvalidated-icon.svg';
import warningValidationIcon from 'src/images/validation/warning-icon.svg';
import warningValidationBullet from 'src/images/validation/warningValidationBullet.svg';
import { getUnEmbeddedFilePages } from 'src/utils/getUnEmbeddedFonts';
import { text } from 'src/utils/localizationUtil';

export type FileRequirementBulletProps = {
  requirement: Validation;
  isMobile: boolean;
};

const BulletSvgForValidationState = (validationState: ValidationState, isClientValidation: boolean) => {
  switch (validationState) {
    case ValidationState.Ok:
      return successfulValidationBullet;
    case ValidationState.Warning:
      return isClientValidation ? warningValidationIcon : warningValidationBullet;
    case ValidationState.Error:
      return isClientValidation ? errorValidationIcon : failedValidationBullet;
    case ValidationState.UnValidated:
      return unvalidatedBullet;
    default:
      return unvalidatedBullet;
  }
};

export const FileRequirementBullet = ({ requirement, isMobile }: FileRequirementBulletProps): ReactElement => {
  const { isClientValidationEnabled } = useClientValidation();
  const { isPerfectBound, getPerfectBoundPageCountTokenValues } = usePerfectBound();
  const tokenValues = isPerfectBound ? getPerfectBoundPageCountTokenValues(requirement) : requirement.tokenValues;

  const { validations } = useValidationContext();
  const containsUnEmbeddedValidation = validations?.find(
    validation => validation.id === ValidatorType.UnEmbeddedFontsValidator && validation.state === ValidationState.Error
  );
  const unEmbeddedPages = containsUnEmbeddedValidation?.details?.pagesData;

  const bulletText = text(requirement.title, tokenValues);

  const validationErrorMessage =
    containsUnEmbeddedValidation && text(requirement.message, { pageNumbers: getUnEmbeddedFilePages(unEmbeddedPages) });

  const messageText =
    requirement.id === ValidatorType.UnEmbeddedFontsValidator
      ? validationErrorMessage
      : requirement.message && text(requirement.message, tokenValues);

  const bullet = BulletSvgForValidationState(requirement.state, isClientValidationEnabled);

  const iconDimension = isMobile ? 20 : 24;

  const textColor = useMemo<StyleTextColor>(() => {
    if (requirement.state === ValidationState.Error) {
      return 'error';
    }
    if (requirement.state === ValidationState.Warning) {
      return 'warning';
    }
    return 'subtle';
  }, [requirement.state]);

  return (
    <Li mb="3">
      {requirement.state === ValidationState.Validating ? (
        <Spinner accessibleText={text('loading')} mr="2" />
      ) : (
        <BulletListIcon
          src={bullet}
          width={iconDimension}
          height={iconDimension}
          alt={text('image-alt-message.bullet-icon')}
        />
      )}
      <BulletListContent>
        <Typography fontSkin="body-standard">{bulletText}</Typography>
        {messageText && (
          <Typography fontSkin="body-small" textColor={textColor}>
            {messageText}
          </Typography>
        )}
      </BulletListContent>
    </Li>
  );
};
